import {
  Box,
  Card,
  CardContent,
  Collapse,
  IconButton,
  IconButtonProps,
  styled
} from '@mui/material';
import React, { ReactNode } from 'react';
import { Subheading } from '../typography/PrimaryTypography';
import { Close } from '@mui/icons-material';

interface CollapseCardProps {
  children: ReactNode;
  title: string;
  expanded?: boolean;
  handleChangeExpanded?: (expanded: boolean) => void;
  icon?: ReactNode;
  flat?: boolean;
}

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

const ExpandMore = styled(
  (props: ExpandMoreProps) => {
    return <IconButton {...props} />;
  },
  {
    shouldForwardProp: (prop: string) => prop !== 'expand'
  }
)(({ theme, expand }) => ({
  boxSizing: 'border-box',
  color: theme.palette.primary.main,
  transform: !expand ? 'rotate(-45deg)' : 'rotate(0deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest
  })
}));

/**
 * Reusable, styled, collapsing card with dynamic header and child content.
 * @param {string} title Header to show for expanded and collapsed card.
 * @param {ReactNode} children Content to show in expanded card section.
 * @param {boolean | undefined} expanded Optionally control expanded state from parent.
 * @param {void | undefined} handleChangeExpanded Optionally callback expanded state to parent if controlled.
 * @param {ReactNode | undefined} icon Optionally display an icon in the Header section of the card.
 * @param {boolean | undefined} flat Optionally style card to have no border or shadow as flat against the background.
 * @returns {JSX.Element}
 */
const CollapseCard = ({
  title,
  children,
  expanded,
  handleChangeExpanded,
  icon,
  flat
}: CollapseCardProps) => {
  const [stateExpanded, setStateExpanded] = React.useState<boolean>(expanded ?? false);

  return (
    <Card sx={{ boxShadow: flat ? 'none' : undefined }}>
      <Box
        sx={{ display: 'flex', cursor: 'pointer', p: 2 }}
        onClick={() => {
          setStateExpanded(expanded === undefined ? !stateExpanded : !expanded);
          expanded !== undefined && handleChangeExpanded && handleChangeExpanded(!expanded);
        }}
      >
        {icon && icon}
        <Subheading weight="Bold">{title}</Subheading>
        <ExpandMore
          expand={expanded ?? stateExpanded}
          aria-expanded={expanded}
          aria-label="show more"
          disableRipple={true}
        >
          <Close />
        </ExpandMore>
      </Box>

      <Collapse in={expanded ?? stateExpanded} timeout="auto" unmountOnExit>
        <CardContent>{children}</CardContent>
      </Collapse>
    </Card>
  );
};

export default CollapseCard;
